import clsx from 'clsx';
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

type Variant = 'primary' | 'success' | 'danger' | 'warning' | 'info' | 'glass';
type Size = 'sm' | 'md';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
}

const VARIANT: Record<Variant, string> = {
  primary: 'bg-gradient-to-br from-accent to-[#e07b00] text-white shadow hover:-translate-y-px hover:shadow-lg',
  success: 'bg-gradient-to-br from-success to-[#16a34a] text-white',
  danger: 'bg-gradient-to-br from-danger to-[#dc2626] text-white',
  warning: 'bg-gradient-to-br from-warning to-[#d97706] text-white',
  info: 'bg-gradient-to-br from-info to-[#2563eb] text-white',
  glass:
    'bg-white/[0.04] border border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-accent/10 hover:text-accent',
};

const SIZE: Record<Size, string> = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'primary', size = 'md', icon, className, children, type = 'button', disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      className={clsx(
        // Every variant has a 1px border (transparent unless glass sets a colour), so all
        // buttons of a size are the same height side by side.
        'inline-flex items-center gap-1.5 rounded-lg border border-transparent font-bold transition',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
});
